
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession, useHasRole } from '@client/shared/auth/index.ts';
import { Button, Card, Heading, PageHeader, Row, Stack, Text } from '@client/shared/design';
import {
  fetchGroupUsers,
  GROUP_USERS_QUERY_KEY,
  type GroupUser,
} from './user-operations.ts';
import { InviteUserForm } from './InviteUserForm.tsx';
import { UserRow } from './UserRow.tsx';
import styles from './Users.module.css';

export function UsersPage() {
  const { session } = useSession();
  const canManage = useHasRole('admin', 'owner');
  const [inviting, setInviting] = useState(false);

  const query = useQuery({
    queryKey: GROUP_USERS_QUERY_KEY,
    queryFn: ({ signal }) => fetchGroupUsers(signal),
    enabled: canManage,
  });

  const selfId = session?.user.id;
  const sorted = useMemo(() => sortUsers(query.data ?? []), [query.data]);

  if (!canManage) {
    return (
      <Card pad="lg" spine>
        <Stack gap={2}>
          <Heading level={3}>Not available</Heading>
          <Text tone="soft">User management is available to admins and the group owner.</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap={6}>
      <PageHeader
        eyebrow="People"
        title="Users"
        description="Invite teammates, set what they can do, and deactivate accounts that no longer need access."
        actions={
          !inviting && (
            <Button leadingIcon={<PlusIcon />} onClick={() => setInviting(true)}>
              Invite user
            </Button>
          )
        }
      />

      {inviting && (
        <InviteUserForm
          onInvited={() => setInviting(false)}
          onCancel={() => setInviting(false)}
        />
      )}

      <UsersBody
        loading={query.isLoading}
        error={query.isError}
        onRetry={() => void query.refetch()}
        users={sorted}
        selfId={selfId}
        onStartInvite={() => setInviting(true)}
      />
    </Stack>
  );
}

interface UsersBodyProps {
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  users: GroupUser[];
  selfId: string | undefined;
  onStartInvite: () => void;
}

function UsersBody({ loading, error, onRetry, users, selfId, onStartInvite }: UsersBodyProps) {
  if (loading) {
    return (
      <Card pad="lg" spine aria-busy="true">
        <Stack gap={3}>
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.skeleton} style={{ ['--i' as string]: i }} />
          ))}
        </Stack>
      </Card>
    );
  }

  if (error) {
    return (
      <Card pad="lg" spine>
        <Stack gap={3}>
          <Heading level={3}>Couldn’t load users</Heading>
          <Text tone="soft">Something went wrong loading the users list. Try again in a moment.</Text>
          <Row>
            <Button variant="secondary" onClick={onRetry}>
              Try again
            </Button>
          </Row>
        </Stack>
      </Card>
    );
  }

  if (users.length === 0) {
    return (
      <Card pad="lg" spine>
        <Stack gap={3} align="start">
          <Heading level={3}>No users yet</Heading>
          <Text tone="soft">
            Invite your first teammate to get started. They’ll receive an email with a link to sign in.
          </Text>
          <Button onClick={onStartInvite}>Invite user</Button>
        </Stack>
      </Card>
    );
  }

  return (
    <Card pad="none" spine>
      <ul className={styles.list} aria-label="Users in your group">
        {users.map((user) => (
          <UserRow key={user.id} user={user} isSelf={user.id === selfId} />
        ))}
      </ul>
    </Card>
  );
}

export function sortUsers(users: readonly GroupUser[]): GroupUser[] {
  const band = (u: GroupUser) => (!u.isActive ? 2 : u.pending ? 1 : 0);
  return [...users].sort((a, b) => band(a) - band(b) || a.name.localeCompare(b.name));
}

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
